import padStart from '../filters/pad-start'

export function createSimpleFunctional (c, el = 'div') {
  return {
    functional: true,
    
    render: (h, { data, children }) => {
      data.staticClass = data.staticClass ? `${c} ${data.staticClass}` : c
      
      return h(el, data, children)
    }
  }
}

export function createSimpleTransition (name) {
  return {
    functional: true,
    
    render (h, context) {
      const origin = (context.data.attrs || context.data.props || {}).origin || 'top center 0'
      context.data = context.data || {}
      context.data.props = { name }
      context.data.on = context.data.on || {}
      
      context.data.on.beforeEnter = el => {
        el.style.transformOrigin = origin
        el.style.webkitTransformOrigin = origin
      }
      
      return h('transition', context.data, context.children)
    }
  }
}

export function directiveConfig (binding, defaults = {}) {
  return Object.assign({},
    defaults,
    binding.modifiers,
    { value: binding.arg },
    binding.value || {}
  )
}

export function closestParentTag (tag) {
  let parent = this.$parent
  
  while (parent) {
    if (!parent.$options._componentTag) return null
    if (parent.$options._componentTag === tag) return parent
    
    parent = parent.$parent
  }
  
  return null
}

export function addOnceEventListener (el, event, cb) {
  var once = () => {
    cb()
    el.removeEventListener(event, once, false)
  }
  
  el.addEventListener(event, once, false)
}

var cachedTransformResult = null;

export function prefixedTransform () {
  if (cachedTransformResult) return cachedTransformResult;
  
  let t = 'transform';
  let testEl = document.createElement('div');
  if (testEl.style.transform == null) {
    let vendors = ['Webkit', 'Moz', 'ms'];
    for (let i in vendors) {
      if (testEl.style[vendors[i] + 'Transform'] !== undefined) {
        t = vendors[i] + 'Transform';
        break;
      }
    }
  }
  
  cachedTransformResult = t;
  return cachedTransformResult;
}

/*export function browserTransform (el, value) {
  [
    'transform',
    'webkitTransform'
  ].forEach(i => {
    el.style[i] = value
  })
}*/

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `execAsap` is passed, trigger the function on the
// leading edge, instead of the trailing.
//
// Example:
// var calculateLayout = function () { ... }
// window.addEventListner('resize', debounce(calculateLayout, 300)
export function debounce (func, threshold = 100, execAsap) {
  var timeout
  
  return function debounced () {
    var obj = this
    var args = arguments
    
    function delayed () {
      if (!execAsap) func.apply(obj, args)
      timeout = null
    }
    
    if (timeout) clearTimeout(timeout)
    else if (execAsap) func.apply(obj, args)
    
    timeout = setTimeout(delayed, threshold)
    return timeout // return timeout so user can cancel debounced calls 
  }
}


/*export function getObjectValueByPath (obj, path) {
  // credit: http://stackoverflow.com/questions/6491463/accessing-nested-javascript-objects-with-string-key#comment55278413_6491621
  if (!path || path.constructor !== String) return
  path = path.replace(/\[(\w+)\]/g, '.$1') // convert indexes to properties
  path = path.replace(/^\./, '')           // strip a leading dot
  let a = path.split('.')
  for (var i = 0, n = a.length; i < n; ++i) {
    var k = a[i]
    if (obj.constructor === Object && k in obj) {
      obj = obj[k]
    } else {
      return
    }
  }
  return obj
}*/

export function findChildComponents (component, filter, result = []) {
  for (var i = 0; i < component.$children.length; i++) {
    let child = component.$children[i];
    if (!filter || filter(child) === true) {
      result.push(child);
    }
    if (child.$children) {
      findChildComponents(child, filter, result)
    }
  }
  return result;
}

export function normalise (value, min, max) {
  return (value - min) / (max - min)
}

export function randRange (min, max) {
  return min + Math.random() * (max - min)
}

export function randomInt (min, max) {
  return Math.round(randRange(min, max))
}

export function randomItem (list) {
  return list[Math.floor(randRange(0, list.length))]
}

// https://stackoverflow.com/a/2450976
export function shuffle (array) {
  let currentIndex = array.length, randomIndex;
  
  while (currentIndex != 0) {
    
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }
  
  return array;
}

/**
 * Converts a timestamp integer or a Date object into a DOMString representing a date in YYYY-MM-DD format, or empty string
 * Useful for usage with <input type='date'>
 */
export function dateDomStringFromDate (date) {
  if (date == null || date == 0) return ''
  if (date.getDate === undefined) date = new Date(date) // convert timestamp into a date object
  let pad = (val) => padStart(val, '00')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Converts a timestamp integer or a Date object into a DOMString representing a date in YYYY-MM-DDTHH:MM format, or empty string
 * Useful for usage with <input type='datetime-local'>
 */
export function dateTimeDomStringFromDate (date) {
  if (date == null || date == 0) return ''
  if (date.getDate === undefined) date = new Date(date) // convert timestamp into a date object
  
  // https://stackoverflow.com/a/62845336/974399
  return new Date(date.getTime() + new Date().getTimezoneOffset() * -60 * 1000).toISOString().slice(0, 16) // drop millisecond info
}

/**
 * Converts a millisecond offset (offset from the start of a day) into a DOMString representing a time in hh:mm format, or empty string
 * Useful for usage with <input type='time'>
 */
export function timeDomStringFromOffset (offsetInMs) {
  if (offsetInMs === null || offsetInMs === undefined) return ''
  
  let date = new Date()
  date.setHours(0, 0, 0, 0)
  date.setMilliseconds(offsetInMs)
  
  return `${padStart(date.getHours())}:${padStart(date.getMinutes())}`
}

export function humanReadableDuration (ms) {
  
  const days = Math.floor(ms / (24 * 60 * 60 * 1000))
    , daysms = ms % (24 * 60 * 60 * 1000)
    , hours = Math.floor(daysms / (60 * 60 * 1000))
    , hoursms = ms % (60 * 60 * 1000)
    , minutes = Math.floor(hoursms / (60 * 1000))
    , minutesms = ms % (60 * 1000)
    , sec = Math.floor(minutesms / 1000)
  
  const getValueLabel = (num, identifier) => (num === 1 ? `${num} ${identifier}` : `${num} ${identifier}s`)
  
  const label = [{ label: 'day', value: days }, { label: 'hour', value: hours }, { label: 'minute', value: minutes }]
    .filter(l => l.value > 0)
    .map(l => getValueLabel(l.value, l.label))
  
  if (label.length === 0) {
    return null
  }
  
  return label.join(', ')
}

export const millisecondsFromTimeOffset = (h, m, s = 0) => (h * 60 * 60 + m * 60 + s) * 1000

export const millisecondsFromTimeDomString = (s) => millisecondsFromTimeOffset(...s.split(':'))
